/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
    FinancialAdjustment,
    IBillingSubtypeDom,
    ICodDomesticWithSubtype,
    ICodInternationalWithSubtype,
    ICodWithSubtype,
    IDeliverySubtypeDom,
    IDeliverySubtypeInt,
    IDocumentationSubtypeInt,
    IEskerSubtypeDom,
    IInvoiceSubtypeDom,
    IInvoiceSubtypeInt,
    ILargeInstrumentWarehouseSubtypeDom,
    IOrderEntrySubtypeDom,
    IOrderEntrySubtypeInt,
    IProductDamageSubtypeDom,
    IProductDamageSubtypeInt,
    IProductDatingSubtypeDom,
    IProductDatingSubtypeInt,
    IQualitySubtypeInt,
    IQuantitySubtypeDom,
    IQuantitySubtypeInt,
    ITemperatureSubtypeDom,
    ITemperatureSubtypeInt,
    NatureOfComplaint,
    NatureOfComplaintDomestic,
    NatureOfComplaintInternational,
    ReferenceNumberTypeDomestic,
    ReferenceNumberTypeInternational,
    Region
} from '@cod-monorepo/interfaces';
import { CodAttachment, CodCreateForm } from '../models/cod-create.model';
import { GeneralCod } from '../models/cod.model';
import { CodOperations } from './cod.operations';
import { toOrderLineDto } from '../models/selectable-order-line.model';

type CodDocument = {
    file: File;
    name: string;
    key: string;
};

export class CodMappingOperations {
    public static mapFormToDto<T extends { issueType: string }>(
        form: CodCreateForm
    ): {
        cod: ICodWithSubtype<T>;
        documents: CodDocument[];
    } {
        const dto: Partial<ICodWithSubtype<T>> = {};
        const documents: CodDocument[] = [];

        const region = form.region.region;
        const domestic = region === Region.NAM;

        dto.region = region;

        if (domestic) {
            CodMappingOperations.mapDomesticCod(form, documents, dto);
        } else {
            CodMappingOperations.mapInternationalCod(form, documents, dto);
        }

        return { cod: dto as ICodWithSubtype<T>, documents };
    }

    // Domestic Mapping Functions

    private static mapDomesticCod<T extends { issueType: string }>(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodWithSubtype<T>>
    ): void {
        dto.natureOfComplaint = `Domestic ${form.referenceNumberDomestic.natureOfComplaint}` as NatureOfComplaint;
        dto.referenceNumber = form.referenceNumberDomestic.referenceNumber;
        dto.referenceNumberType = form.referenceNumberDomestic.referenceNumberType;
        dto.affectedOrderLines = form.affectedItems.affectedOrderLines.map(toOrderLineDto);
        dto.productDiscrepancy = form.referenceNumberDomestic.productDiscrepancy;
        dto.shipToCountryCode = form.affectedItems.affectedOrderLines.length
            ? form.affectedItems.affectedOrderLines[0].shipToCountryCode
            : form.affectedItems.shipToCountryCode;

        CodMappingOperations.mapDomesticNatureOfComplaint(form, documents, dto);
    }

    private static mapDomesticNatureOfComplaint<T extends { issueType: string }>(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodWithSubtype<T>>
    ): void {
        const natureOfComplaint = form.referenceNumberDomestic.natureOfComplaint;

        switch (natureOfComplaint) {
            case NatureOfComplaintDomestic.PRODUCT_DAMAGE:
                CodMappingOperations.mapDomesticProductDamage(
                    form,
                    documents,
                    dto as unknown as ICodWithSubtype<IProductDamageSubtypeDom>
                );
                break;
            case NatureOfComplaintDomestic.ORDER_ENTRY:
                CodMappingOperations.mapDomesticOrderEntry(
                    form,
                    documents,
                    dto as unknown as ICodWithSubtype<IOrderEntrySubtypeDom>
                );
                break;
            case NatureOfComplaintDomestic.ITEM_OR_QUANTITY:
                CodMappingOperations.mapDomesticItemOrQuantity(
                    form,
                    documents,
                    dto as unknown as ICodWithSubtype<IQuantitySubtypeDom>
                );
                break;
            case NatureOfComplaintDomestic.PRODUCT_DATING:
                CodMappingOperations.mapDomesticProductDating(
                    form,
                    documents,
                    dto as ICodWithSubtype<IProductDatingSubtypeDom>
                );
                break;
            case NatureOfComplaintDomestic.PRODUCT_TEMPERATURE:
                CodMappingOperations.mapDomesticProductTemperature(
                    form,
                    documents,
                    dto as unknown as ICodWithSubtype<ITemperatureSubtypeDom>
                );
                break;
            case NatureOfComplaintDomestic.DELIVERY:
                CodMappingOperations.mapDomesticDeliveryError(
                    form,
                    documents,
                    dto as ICodWithSubtype<IDeliverySubtypeDom>
                );
                break;
            case NatureOfComplaintDomestic.INVOICE_ERROR:
                CodMappingOperations.mapDomesticInvoiceError(
                    form,
                    documents,
                    dto as unknown as ICodWithSubtype<IInvoiceSubtypeDom>
                );
                break;
            case NatureOfComplaintDomestic.BILLING_ADJUSTMENT:
                CodMappingOperations.mapDomesticBillingAdjustment(
                    form,
                    documents,
                    dto as ICodWithSubtype<IBillingSubtypeDom>
                );
                break;
            case NatureOfComplaintDomestic.ESKER:
                CodMappingOperations.mapDomesticEskerDiscrepancy(
                    form,
                    documents,
                    dto as ICodWithSubtype<IEskerSubtypeDom>
                );
                break;
            case NatureOfComplaintDomestic.LARGE_INSTRUMENT_WAREHOUSE:
                CodMappingOperations.mapDomesticLargeInstrumentWarehouseDiscrepancy(
                    form,
                    documents,
                    dto as unknown as ICodWithSubtype<ILargeInstrumentWarehouseSubtypeDom>
                );
                break;
            default:
                break;
        }
    }

    private static mapDomesticProductDamage(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: ICodDomesticWithSubtype<IProductDamageSubtypeDom>
    ): void {
        dto.financialAdjustmentValue = form.productDamageDomestic.adjustmentAmount;
        dto.relatedTrackingNumber = form.productDamageDomestic.callTagTrackingNumber;
        dto.additionalInfo = form.productDamageDomestic.additionalInfo;
        dto.relatedNumber = form.productDamageDomestic.creditNumber;
        dto.managerGid = form.productDamageDomestic.managerApprovedRequest;
        dto.filedWithin30Days = form.productDamageDomestic.filedWithin30Days;
        dto.subType = {
            issueType: form.productDamageDomestic.damageType,
            conditionOfItem: form.productDamageDomestic.itemCondition,
            itemType: form.productDamageDomestic.itemType,
            additionalPackagingInfo: form.productDamageDomestic.additionalPackagingInfo,
            damageClaimFiled: form.productDamageDomestic.damageClaimFiled,
            documentationNotedIssue: form.productDamageDomestic.documentationNotedIssue,
            packageType: form.productDamageDomestic.packageType,
            packageTypeCondition: form.productDamageDomestic.packageTypeCondition,
            protectivePackagingUsed: form.productDamageDomestic.protectivePackagingUsed
        };

        CodMappingOperations.addDocuments(
            'deliveryDocumentation',
            form.productDamageDomestic.deliveryDocumentation,
            documents
        );
        CodMappingOperations.addDocuments('relatedDocuments', form.productDamageDomestic.relatedDocuments, documents);
        CodMappingOperations.addDocuments('visualEvidence', form.productDamageDomestic.visualEvidence, documents);
    }

    private static mapDomesticProductDating(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: ICodDomesticWithSubtype<IProductDatingSubtypeDom>
    ): void {
        dto.financialAdjustmentValue = form.productDatingDomestic.adjustmentAmount;
        dto.relatedTrackingNumber = form.productDatingDomestic.callTagTrackingNumber;
        dto.additionalInfo = form.productDatingDomestic.additionalInfo;
        dto.relatedNumber = form.productDatingDomestic.creditNumber;
        dto.managerGid = form.productDatingDomestic.managerApprovedRequest;
        dto.filedWithin30Days = form.productDatingDomestic.filedWithin30Days;
        dto.subType = {
            issueType: form.productDatingDomestic.issueType
        };

        CodMappingOperations.addDocuments('relatedDocuments', form.productDatingDomestic.relatedDocuments, documents);
    }

    private static mapDomesticOrderEntry(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: ICodDomesticWithSubtype<IOrderEntrySubtypeDom>
    ): void {
        dto.financialAdjustmentValue = form.orderEntryDomestic.adjustmentAmount;
        dto.relatedTrackingNumber = form.orderEntryDomestic.callTagTrackingNumber;
        dto.additionalInfo = form.orderEntryDomestic.additionalInfo;
        dto.relatedNumber = form.orderEntryDomestic.creditNumber;
        dto.managerGid = form.orderEntryDomestic.managerApprovedRequest;
        dto.filedWithin30Days = form.orderEntryDomestic.filedWithin30Days;
        dto.subType = {
            issueType: form.orderEntryDomestic.issueType,
            accountErrorType: form.orderEntryDomestic.accountErrorType,
            csrGID: form.orderEntryDomestic.csrResponsible,
            correctOrderType: form.orderEntryDomestic.correctOrderType,
            orderTypeIncorrectlyUsed: form.orderEntryDomestic.incorrectlyUsed
        };

        CodMappingOperations.addDocuments('relatedDocuments', form.orderEntryDomestic.relatedDocuments, documents);
    }

    private static mapDomesticItemOrQuantity(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: ICodDomesticWithSubtype<IQuantitySubtypeDom>
    ): void {
        dto.financialAdjustmentValue = form.itemOrQuantityDomestic.adjustmentAmount;
        dto.relatedTrackingNumber = form.itemOrQuantityDomestic.callTagTrackingNumber;
        dto.additionalInfo = form.itemOrQuantityDomestic.additionalInfo;
        dto.relatedNumber = form.itemOrQuantityDomestic.creditNumber;
        dto.managerGid = form.itemOrQuantityDomestic.managerApprovedRequest;
        dto.filedWithin30Days = form.itemOrQuantityDomestic.filedWithin30Days;
        dto.subType = {
            issueType: form.itemOrQuantityDomestic.issueType,
            unorderedItemQuantity: form.itemOrQuantityDomestic.unorderedItemQuantity,
            unorderedItemSmn: form.itemOrQuantityDomestic.unorderedItemSmn
        };

        CodMappingOperations.addDocuments('relatedDocuments', form.itemOrQuantityDomestic.relatedDocuments, documents);
    }

    private static mapDomesticProductTemperature(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: ICodDomesticWithSubtype<ITemperatureSubtypeDom>
    ): void {
        dto.financialAdjustmentValue = form.productTemperatureDomestic.adjustmentAmount;
        dto.relatedTrackingNumber = form.productTemperatureDomestic.callTagTrackingNumber;
        dto.additionalInfo = form.productTemperatureDomestic.additionalInfo;
        dto.relatedNumber = form.productTemperatureDomestic.creditNumber;
        dto.managerGid = form.productTemperatureDomestic.managerApprovedRequest;
        dto.filedWithin30Days = form.productTemperatureDomestic.filedWithin30Days;
        dto.subType = {
            issueType: form.productTemperatureDomestic.issueType,
            conditionOfItem: form.productTemperatureDomestic.itemCondition,
            explanation: form.productTemperatureDomestic.temperatureExcursionExplanation
        };

        CodMappingOperations.addDocuments(
            'relatedDocuments',
            form.productTemperatureDomestic.relatedDocuments,
            documents
        );
        CodMappingOperations.addDocuments(
            'temperatureExcursionEvidence',
            form.productTemperatureDomestic.temperatureExcursionEvidence,
            documents
        );
    }

    private static mapDomesticDeliveryError(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: ICodDomesticWithSubtype<IDeliverySubtypeDom>
    ): void {
        dto.financialAdjustmentValue = form.deliveryErrorDomestic.adjustmentAmount;
        dto.relatedTrackingNumber = form.deliveryErrorDomestic.callTagTrackingNumber;
        dto.additionalInfo = form.deliveryErrorDomestic.additionalInfo;
        dto.relatedNumber = form.deliveryErrorDomestic.creditNumber;
        dto.managerGid = form.deliveryErrorDomestic.managerApprovedRequest;
        dto.filedWithin30Days = form.deliveryErrorDomestic.filedWithin30Days;
        dto.subType = {
            issueType: form.deliveryErrorDomestic.issueType,
            deliveryDocumentationIncorrectAddress: form.deliveryErrorDomestic.deliveryDocumentationMatchesAddress,
            incorrectDeliveryAddress: form.deliveryErrorDomestic.addressShippedTo
        };

        CodMappingOperations.addDocuments('relatedDocuments', form.deliveryErrorDomestic.relatedDocuments, documents);
    }

    private static mapDomesticInvoiceError(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: ICodDomesticWithSubtype<IInvoiceSubtypeDom>
    ): void {
        dto.financialAdjustmentValue = form.invoiceErrorDomestic.adjustmentAmount;
        dto.relatedTrackingNumber = form.invoiceErrorDomestic.callTagTrackingNumber;
        dto.additionalInfo = form.invoiceErrorDomestic.additionalInfo;
        dto.relatedNumber = form.invoiceErrorDomestic.creditNumber;
        dto.managerGid = form.invoiceErrorDomestic.managerApprovedRequest;
        dto.filedWithin30Days = form.invoiceErrorDomestic.filedWithin30Days;
        dto.subType = {
            issueType: form.invoiceErrorDomestic.invoiceErrorType,
            invoiceErrorType: form.invoiceErrorDomestic.invoiceErrorDetailType,
            instructionsContained: form.invoiceErrorDomestic.instructionsContained
        };

        CodMappingOperations.addDocuments('relatedDocuments', form.invoiceErrorDomestic.relatedDocuments, documents);
    }

    private static mapDomesticBillingAdjustment(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: ICodDomesticWithSubtype<IBillingSubtypeDom>
    ): void {
        dto.financialAdjustmentValue = form.billingAdjustmentDomestic.adjustmentAmount;
        dto.relatedTrackingNumber = form.billingAdjustmentDomestic.callTagTrackingNumber;
        dto.additionalInfo = form.billingAdjustmentDomestic.additionalInfo;
        dto.relatedNumber = form.billingAdjustmentDomestic.creditNumber;
        dto.managerGid = form.billingAdjustmentDomestic.managerApprovedRequest;
        dto.filedWithin30Days = form.billingAdjustmentDomestic.filedWithin30Days;
        dto.subType = {
            issueType: form.billingAdjustmentDomestic.issueType
        };

        CodMappingOperations.addDocuments(
            'relatedDocuments',
            form.billingAdjustmentDomestic.relatedDocuments,
            documents
        );
    }

    private static mapDomesticEskerDiscrepancy(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: ICodDomesticWithSubtype<IEskerSubtypeDom>
    ): void {
        dto.financialAdjustmentValue = form.eskerDiscrepancyDomestic.adjustmentAmount;
        dto.relatedTrackingNumber = form.eskerDiscrepancyDomestic.callTagTrackingNumber;
        dto.additionalInfo = form.eskerDiscrepancyDomestic.additionalInfo;
        dto.relatedNumber = form.eskerDiscrepancyDomestic.creditNumber;
        dto.managerGid = form.eskerDiscrepancyDomestic.managerApprovedRequest;
        dto.filedWithin30Days = form.eskerDiscrepancyDomestic.filedWithin30Days;
        dto.subType = {
            issueType: form.eskerDiscrepancyDomestic.issueType
        };

        CodMappingOperations.addDocuments(
            'relatedDocuments',
            form.eskerDiscrepancyDomestic.relatedDocuments,
            documents
        );
    }

    private static mapDomesticLargeInstrumentWarehouseDiscrepancy(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: ICodDomesticWithSubtype<ILargeInstrumentWarehouseSubtypeDom>
    ): void {
        dto.financialAdjustmentValue = form.largeInstrumentWarehouseDiscrepancy.adjustmentAmount;
        dto.relatedTrackingNumber = form.largeInstrumentWarehouseDiscrepancy.callTagTrackingNumber;
        dto.additionalInfo = form.largeInstrumentWarehouseDiscrepancy.additionalInfo;
        dto.relatedNumber = form.largeInstrumentWarehouseDiscrepancy.creditNumber;
        dto.managerGid = form.largeInstrumentWarehouseDiscrepancy.managerApprovedRequest;
        dto.filedWithin30Days = form.largeInstrumentWarehouseDiscrepancy.filedWithin30Days;
        dto.subType = {
            issueType: form.largeInstrumentWarehouseDiscrepancy.issueType,
            claimFiled: form.largeInstrumentWarehouseDiscrepancy.claimFiled,
            conditionOfItem: form.largeInstrumentWarehouseDiscrepancy.itemCondition,
            packageType: form.largeInstrumentWarehouseDiscrepancy.packageType,
            packageTypeCondition: form.largeInstrumentWarehouseDiscrepancy.packageTypeCondition,
            potentialIssueNoted: form.largeInstrumentWarehouseDiscrepancy.documentationNotedIssue,
            additionalPackagingInfo: form.largeInstrumentWarehouseDiscrepancy.additionalPackagingInfo
        };

        CodMappingOperations.addDocuments(
            'relatedDocuments',
            form.largeInstrumentWarehouseDiscrepancy.relatedDocuments,
            documents
        );

        CodMappingOperations.addDocuments(
            'carrierClaimDocumentation',
            form.largeInstrumentWarehouseDiscrepancy.carrierClaimDocumentation,
            documents
        );

        CodMappingOperations.addDocuments(
            'deliveryDocumentation',
            form.largeInstrumentWarehouseDiscrepancy.deliveryDocumentation,
            documents
        );

        CodMappingOperations.addDocuments(
            'visualEvidence',
            form.largeInstrumentWarehouseDiscrepancy.visualEvidence,
            documents
        );
    }

    // International Mapping Functions

    private static mapInternationalCod<T extends { issueType: string }>(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodInternationalWithSubtype<T>>
    ): void {
        dto.natureOfComplaint =
            `International ${form.referenceNumberInternational.natureOfComplaint}` as NatureOfComplaint;
        dto.referenceNumber = form.referenceNumberInternational.referenceNumber;
        dto.referenceNumberType = form.referenceNumberInternational.referenceNumberType;
        dto.affectedOrderLines = form.affectedItems.affectedOrderLines.map(toOrderLineDto);
        dto.productDiscrepancy = form.referenceNumberInternational.productDiscrepancy;
        dto.pgiWithin40Days = form.referenceNumberInternational.pgiWithin40Days;
        dto.shipToCountryCode = form.affectedItems.affectedOrderLines.length
            ? form.affectedItems.affectedOrderLines[0].shipToCountryCode
            : form.affectedItems.shipToCountryCode;

        CodMappingOperations.mapInternationalNatureOfComplaint(form, documents, dto);

        dto.financialAdjustmentValue = CodOperations.calculateFinancialAdjustmentValue(dto as GeneralCod);
    }

    private static mapInternationalNatureOfComplaint<T extends { issueType: string }>(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodInternationalWithSubtype<T>>
    ): void {
        const natureOfComplaint = form.referenceNumberInternational.natureOfComplaint;

        switch (natureOfComplaint) {
            case NatureOfComplaintInternational.PRODUCT_DAMAGE:
                CodMappingOperations.mapInternationalProductDamage(
                    form,
                    documents,
                    dto as unknown as ICodInternationalWithSubtype<IProductDamageSubtypeInt>
                );
                break;
            case NatureOfComplaintInternational.ORDER_ENTRY:
                CodMappingOperations.mapInternationalOrderEntry(
                    form,
                    documents,
                    dto as ICodInternationalWithSubtype<IOrderEntrySubtypeInt>
                );
                break;
            case NatureOfComplaintInternational.ITEM_OR_QUANTITY:
                CodMappingOperations.mapInternationalItemOrQuantity(
                    form,
                    documents,
                    dto as ICodInternationalWithSubtype<IQuantitySubtypeInt>
                );
                break;
            case NatureOfComplaintInternational.PRODUCT_DATING:
                CodMappingOperations.mapInternationalProductDating(
                    form,
                    documents,
                    dto as ICodInternationalWithSubtype<IProductDatingSubtypeInt>
                );
                break;
            case NatureOfComplaintInternational.PRODUCT_TEMPERATURE:
                CodMappingOperations.mapInternationalProductTemperature(
                    form,
                    documents,
                    dto as unknown as ICodInternationalWithSubtype<ITemperatureSubtypeInt>
                );
                break;
            case NatureOfComplaintInternational.DELIVERY:
                CodMappingOperations.mapInternationalDeliveryError(
                    form,
                    documents,
                    dto as ICodInternationalWithSubtype<IDeliverySubtypeInt>
                );
                break;
            case NatureOfComplaintInternational.INVOICE_ERROR:
                CodMappingOperations.mapInternationalInvoiceError(
                    form,
                    documents,
                    dto as unknown as ICodInternationalWithSubtype<IInvoiceSubtypeInt>
                );
                break;
            case NatureOfComplaintInternational.QUALITY:
                CodMappingOperations.mapInternationalQualityIssue(
                    form,
                    documents,
                    dto as unknown as ICodInternationalWithSubtype<IQualitySubtypeInt>
                );
                break;
            case NatureOfComplaintInternational.DOCUMENTATION:
                CodMappingOperations.mapInternationalDocumentation(
                    form,
                    documents,
                    dto as unknown as ICodInternationalWithSubtype<IDocumentationSubtypeInt>
                );
                break;
            default:
                break;
        }
    }

    private static mapInternationalOrderEntry(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: ICodInternationalWithSubtype<IOrderEntrySubtypeInt>
    ): void {
        dto.financialAdjustmentRequest = form.orderEntryInternational.financialAdjustment;
        dto.subType = {
            issueType: form.orderEntryInternational.issueType,
            accountErrorType: form.orderEntryInternational.accountErrorType,
            canIdentifyCartonId: form.orderEntryInternational.canIdentifyCartonId,
            cartonId: form.orderEntryInternational.cartonId,
            errorDescription: form.orderEntryInternational.errorDescription
        };

        CodMappingOperations.addDocuments('relatedDocuments', form.orderEntryInternational.relatedDocuments, documents);
    }

    private static mapInternationalItemOrQuantity(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodInternationalWithSubtype<IQuantitySubtypeInt>>
    ): void {
        dto.financialAdjustmentRequest =
            form.itemOrQuantityInternational.financialAdjustment ?? FinancialAdjustment.NONE;
        dto.additionalInfo = form.itemOrQuantityInternational.additionalInfo;
        dto.subType = {
            issueType: form.itemOrQuantityInternational.issueType,
            canIdentifyCartonId: form.itemOrQuantityInternational.canIdentifyCartonId,
            cartonId: form.itemOrQuantityInternational.cartonId,
            lotsDeclinedWithin24Hours: form.itemOrQuantityInternational.lotsDeclinedWithin24Hours,
            unorderedItemQuantity: form.itemOrQuantityInternational.unorderedItemQuantity,
            unorderedItemSmn: form.itemOrQuantityInternational.unorderedItemSmn
        };

        CodMappingOperations.addDocuments(
            'relatedDocuments',
            form.itemOrQuantityInternational.relatedDocuments,
            documents
        );

        CodMappingOperations.addDocuments(
            'communicationDeclinedDocuments',
            form.itemOrQuantityInternational.communicationDeclinedDocuments,
            documents
        );
    }

    private static mapInternationalProductDating(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodInternationalWithSubtype<IProductDatingSubtypeInt>>
    ): void {
        dto.financialAdjustmentRequest =
            form.productDatingInternational.financialAdjustment ?? FinancialAdjustment.NONE;
        dto.subType = {
            issueType: form.productDatingInternational.issueType,
            declinedWithin24Hours: form.productDatingInternational.declinedWithin24Hours,
            batchExpiryMeetsMinShipPolicy: form.productDatingInternational.batchExpiryMeetsMinShipPolicy
        };

        CodMappingOperations.addDocuments(
            'relatedDocuments',
            form.productDatingInternational.relatedDocuments,
            documents
        );

        CodMappingOperations.addDocuments(
            'communicationDeclinedDocuments',
            form.productDatingInternational.communicationDeclinedDocuments,
            documents
        );
    }

    private static mapInternationalProductTemperature(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodInternationalWithSubtype<ITemperatureSubtypeInt>>
    ): void {
        dto.financialAdjustmentRequest = form.productTemperatureInternational.financialAdjustment;
        dto.subType = {
            issueType: form.productTemperatureInternational.issueType,
            claimFiled: form.productTemperatureInternational.claimFiled,
            carrierTrackingNumber: form.productTemperatureInternational.carrierTrackingNumber,
            conditionOfItem: form.productTemperatureInternational.itemCondition,
            exactLocation: form.productTemperatureInternational.exactLocation,
            explanation: form.productTemperatureInternational.explainWhyTemperatureExcursionSuspected,
            identificationBy: form.productTemperatureInternational.temperatureExcursionIdentifiedBy,
            identificationDate: new Date(form.productTemperatureInternational.dateOfPotentialTemperatureExcursion),
            identificationLocation: form.productTemperatureInternational.locationOfPotentialTemperatureExcursion,
            issueNotedInDocumentation: form.productTemperatureInternational.documentationNotedIssue,
            temperatureRequirementsIncluded:
                form.productTemperatureInternational.includesShippingTemperatureRequirements,
            issueNotNotedExplanation: form.productTemperatureInternational.explanationOfMissingNote
        };

        CodMappingOperations.addDocuments(
            'relatedDocuments',
            form.productTemperatureInternational.relatedDocuments,
            documents
        );

        CodMappingOperations.addDocuments(
            'carrierClaimDocumentation',
            form.productTemperatureInternational.carrierClaimDocumentation,
            documents
        );

        CodMappingOperations.addDocuments(
            'deliveryDocumentation',
            form.productTemperatureInternational.deliveryDocumentation,
            documents
        );

        CodMappingOperations.addDocuments(
            'temperatureExcursionEvidence',
            form.productTemperatureInternational.temperatureExcursionEvidence,
            documents
        );
    }

    private static mapInternationalDeliveryError(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodInternationalWithSubtype<IDeliverySubtypeInt>>
    ): void {
        dto.financialAdjustmentRequest = form.deliveryErrorInternational.financialAdjustment;
        dto.additionalInfo = form.deliveryErrorInternational.additionalInfo;
        dto.subType = {
            issueType: form.deliveryErrorInternational.issueType,
            ainErrorType: form.deliveryErrorInternational.ainErrorType,
            incorrectDeliveryAddress:
                form.deliveryErrorInternational.addressShippedTo || form.deliveryErrorInternational.portShippedTo,
            incorrectDeliveryInDocumentation:
                form.deliveryErrorInternational.deliveryDocumentationMatchesAddress ||
                form.deliveryErrorInternational.deliveryDocumentationMatchesPort,
            replacementOrderRequired: form.deliveryErrorInternational.replacementOrderRequired,
            replacementOrderNumber: form.deliveryErrorInternational.replacementOrderNumber,
            wrongCarrier: form.deliveryErrorInternational.carrier,
            shipmentNumber: form.deliveryErrorInternational.shipmentNumber
        };

        CodMappingOperations.addDocuments(
            'relatedDocuments',
            form.deliveryErrorInternational.relatedDocuments,
            documents
        );

        CodMappingOperations.addDocuments(
            'deliveryDocumentation',
            form.deliveryErrorInternational.deliveryDocumentation,
            documents
        );

        CodMappingOperations.addDocuments(
            'supportDocumentation',
            form.deliveryErrorInternational.supportDocumentation,
            documents
        );
    }

    private static mapInternationalInvoiceError(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodInternationalWithSubtype<IInvoiceSubtypeInt>>
    ): void {
        dto.financialAdjustmentRequest = form.invoiceErrorInternational.financialAdjustment;
        dto.additionalInfo = form.invoiceErrorInternational.additionalInfo;
        dto.subType = {
            issueType: form.invoiceErrorInternational.invoiceErrorType,
            errorsIdentifiedWithin24hours: form.invoiceErrorInternational.errorsIdentifiedWithin24hours,
            invoiceErrorTypeOtherInfo: form.invoiceErrorInternational.invoiceErrorTypeOtherInfo,
            invoiceMatchesIODC: form.invoiceErrorInternational.invoiceMatchesIODC
        };

        CodMappingOperations.addDocuments(
            'relatedDocuments',
            form.invoiceErrorInternational.relatedDocuments,
            documents
        );

        CodMappingOperations.addDocuments(
            'communicationDocuments',
            form.invoiceErrorInternational.relatedDocuments,
            documents
        );
    }

    private static mapInternationalQualityIssue(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodInternationalWithSubtype<IQualitySubtypeInt>>
    ): void {
        dto.financialAdjustmentRequest = form.qualityInternational.financialAdjustment;
        dto.additionalInfo = form.qualityInternational.additionalInfo;
        dto.subType = {
            issueType: form.qualityInternational.issueType,
            materialDisposition: form.qualityInternational.materialDisposition,
            countrySpecificLabelMissing: form.qualityInternational.countrySpecificLabelMissing,
            labelError: form.qualityInternational.labelError
        };

        CodMappingOperations.addDocuments('relatedDocuments', form.qualityInternational.relatedDocuments, documents);
    }

    private static mapInternationalProductDamage(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodInternationalWithSubtype<IProductDamageSubtypeInt>>
    ): void {
        dto.financialAdjustmentRequest = form.productDamageInternational.financialAdjustment;
        dto.subType = {
            issueType: form.productDamageInternational.damageType,
            carrierTrackingNumber: form.productDamageInternational.carrierTrackingNumber,
            conditionOfItem: form.productDamageInternational.itemCondition,
            itemType: form.productDamageInternational.itemType,
            additionalPackagingInfo: form.productDamageInternational.additionalPackagingInfo,
            canIdentifyCartonId: form.productDamageInternational.canIdentifyCartonId,
            cartonId: form.productDamageInternational.cartonId,
            damageClaimFiled: form.productDamageInternational.damageClaimFiled,
            documentationNotedIssue: form.productDamageInternational.documentationNotedIssue,
            explanationOfMissingNote: form.productDamageInternational.explanationOfMissingNote,
            packageType: form.productDamageInternational.packageType,
            packageTypeCondition: form.productDamageInternational.packageTypeCondition,
            protectivePackagingUsed: form.productDamageInternational.protectivePackagingUsed
        };

        CodMappingOperations.addDocuments(
            'relatedDocuments',
            form.productDamageInternational.relatedDocuments,
            documents
        );

        CodMappingOperations.addDocuments(
            'damageClaimDocumentation',
            form.productDamageInternational.damageClaimDocumentation,
            documents
        );

        CodMappingOperations.addDocuments(
            'deliveryDocumentation',
            form.productDamageInternational.deliveryDocumentation,
            documents
        );

        CodMappingOperations.addDocuments('visualEvidence', form.productDamageInternational.visualEvidence, documents);
    }

    private static mapInternationalDocumentation(
        form: CodCreateForm,
        documents: CodDocument[],
        dto: Partial<ICodInternationalWithSubtype<IDocumentationSubtypeInt>>
    ): void {
        dto.financialAdjustmentRequest = form.documentationInternational.financialAdjustment;
        dto.subType = {
            issueType: form.documentationInternational.issueType,
            documentationIssue: form.documentationInternational.documentationIssue,
            documentationIssueOtherInfo: form.documentationInternational.documentationIssueOther,
            containedSpecialInstructions: form.documentationInternational.specialInstructions,
            documentationError: form.documentationInternational.documentationErrorType,
            documentationErrorOtherInfo: form.documentationInternational.documentationErrorTypeOther
        };

        CodMappingOperations.addDocuments(
            'relatedDocuments',
            form.documentationInternational.relatedDocuments,
            documents
        );
    }

    public static mapDtoToForm(dto: GeneralCod, keys: string[], confirmed = true): CodCreateForm {
        const form: Partial<CodCreateForm> = {
            region: {
                region: dto.region
            },
            affectedItems: {
                affectedOrderLines: dto.affectedOrderLines!,
                shipToCountryCode: dto.shipToCountryCode,
                typeValid: true,
                confirmed
            }
        };

        if (dto.region === Region.NAM) {
            const natureOfComplaint = dto.natureOfComplaint.replace(
                'Domestic ',
                ''
            ) as unknown as NatureOfComplaintDomestic;

            form.referenceNumberDomestic = {
                natureOfComplaint,
                referenceNumber: dto.referenceNumber,
                productDiscrepancy: dto.productDiscrepancy || false,
                referenceNumberType: dto.referenceNumberType as unknown as ReferenceNumberTypeDomestic,
                referenceNumberValid: true
            };

            switch (natureOfComplaint) {
                case NatureOfComplaintDomestic.PRODUCT_DAMAGE:
                    form.productDamageDomestic = this.mapProductDamageDomToForm(
                        dto as ICodDomesticWithSubtype<IProductDamageSubtypeDom>,
                        keys
                    );
                    break;
                case NatureOfComplaintDomestic.ORDER_ENTRY:
                    form.orderEntryDomestic = this.mapOrderEntryDomToForm(
                        dto as ICodDomesticWithSubtype<IOrderEntrySubtypeDom>,
                        keys
                    );
                    break;
                case NatureOfComplaintDomestic.ITEM_OR_QUANTITY:
                    form.itemOrQuantityDomestic = this.mapItemOrQuantityDomToForm(
                        dto as unknown as ICodDomesticWithSubtype<IQuantitySubtypeDom>,
                        keys
                    );
                    break;
                case NatureOfComplaintDomestic.PRODUCT_DATING:
                    form.productDatingDomestic = this.mapProductDatingDomToForm(
                        dto as ICodDomesticWithSubtype<IProductDatingSubtypeDom>,
                        keys
                    );
                    break;
                case NatureOfComplaintDomestic.PRODUCT_TEMPERATURE:
                    form.productTemperatureDomestic = this.mapProductTemperatureDomToForm(
                        dto as ICodDomesticWithSubtype<ITemperatureSubtypeDom>,
                        keys
                    );
                    break;
                case NatureOfComplaintDomestic.DELIVERY:
                    form.deliveryErrorDomestic = this.mapDeliveryDomToForm(
                        dto as unknown as ICodDomesticWithSubtype<IDeliverySubtypeDom>,
                        keys
                    );
                    break;
                case NatureOfComplaintDomestic.INVOICE_ERROR:
                    form.invoiceErrorDomestic = this.mapInvoiceDomToForm(
                        dto as unknown as ICodDomesticWithSubtype<IInvoiceSubtypeDom>,
                        keys
                    );
                    break;
                case NatureOfComplaintDomestic.BILLING_ADJUSTMENT:
                    form.billingAdjustmentDomestic = this.mapBillingAdjustmentDomToForm(
                        dto as unknown as ICodDomesticWithSubtype<IBillingSubtypeDom>,
                        keys
                    );
                    break;
                case NatureOfComplaintDomestic.ESKER:
                    form.eskerDiscrepancyDomestic = this.mapEskerDomToForm(
                        dto as unknown as ICodDomesticWithSubtype<IEskerSubtypeDom>,
                        keys
                    );
                    break;
                case NatureOfComplaintDomestic.LARGE_INSTRUMENT_WAREHOUSE:
                    form.largeInstrumentWarehouseDiscrepancy = this.mapLargeInstrumentWarehouseDomToForm(
                        dto as unknown as ICodDomesticWithSubtype<ILargeInstrumentWarehouseSubtypeDom>,
                        keys
                    );
                    break;
                default:
                    break;
            }
        } else {
            const natureOfComplaint = dto.natureOfComplaint.replace(
                'International ',
                ''
            ) as unknown as NatureOfComplaintInternational;

            form.referenceNumberInternational = {
                natureOfComplaint: natureOfComplaint,
                referenceNumber: dto.referenceNumber,
                productDiscrepancy: dto.productDiscrepancy || false,
                referenceNumberType: dto.referenceNumberType as unknown as ReferenceNumberTypeInternational,
                pgiWithin40Days: dto.pgiWithin40Days || false,
                referenceNumberValid: true
            };

            switch (natureOfComplaint) {
                case NatureOfComplaintInternational.PRODUCT_DAMAGE:
                    form.productDamageInternational = this.mapProductDamageIntToForm(
                        dto as ICodInternationalWithSubtype<IProductDamageSubtypeInt>,
                        keys
                    );
                    break;
                case NatureOfComplaintInternational.ORDER_ENTRY:
                    form.orderEntryInternational = this.mapOrderEntryIntToForm(
                        dto as ICodInternationalWithSubtype<IOrderEntrySubtypeInt>,
                        keys
                    );
                    break;
                case NatureOfComplaintInternational.ITEM_OR_QUANTITY:
                    form.itemOrQuantityInternational = this.mapItemOrQuantityIntToForm(
                        dto as ICodInternationalWithSubtype<IQuantitySubtypeInt>,
                        keys
                    );
                    break;
                case NatureOfComplaintInternational.PRODUCT_DATING:
                    form.productDatingInternational = this.mapProductDatingIntToForm(
                        dto as ICodInternationalWithSubtype<IProductDatingSubtypeInt>,
                        keys
                    );
                    break;
                case NatureOfComplaintInternational.PRODUCT_TEMPERATURE:
                    form.productTemperatureInternational = this.mapProductTemperatureIntToForm(
                        dto as ICodInternationalWithSubtype<ITemperatureSubtypeInt>,
                        keys
                    );
                    break;
                case NatureOfComplaintInternational.DELIVERY:
                    form.deliveryErrorInternational = this.mapDeliveryIntToForm(
                        dto as ICodInternationalWithSubtype<IDeliverySubtypeInt>,
                        keys
                    );
                    break;
                case NatureOfComplaintInternational.INVOICE_ERROR:
                    form.invoiceErrorInternational = this.mapInvoiceIntToForm(
                        dto as ICodInternationalWithSubtype<IInvoiceSubtypeInt>,
                        keys
                    );
                    break;
                case NatureOfComplaintInternational.QUALITY:
                    form.qualityInternational = this.mapQualityIntToForm(
                        dto as ICodInternationalWithSubtype<IQualitySubtypeInt>,
                        keys
                    );
                    break;
                case NatureOfComplaintInternational.DOCUMENTATION:
                    form.documentationInternational = this.mapDocumentationIntToForm(
                        dto as ICodInternationalWithSubtype<IDocumentationSubtypeInt>,
                        keys
                    );
                    break;
                default:
                    break;
            }
        }

        return form as CodCreateForm;
    }

    private static mapProductDamageDomToForm(
        dto: ICodDomesticWithSubtype<IProductDamageSubtypeDom>,
        keys: string[]
    ): CodCreateForm['productDamageDomestic'] {
        return {
            ...CodMappingOperations.commonDomesticMapping(dto),
            damageType: dto.subType.issueType,
            itemCondition: dto.subType.conditionOfItem,
            itemType: dto.subType.itemType,
            additionalPackagingInfo: dto.subType.additionalPackagingInfo!,
            damageClaimFiled: dto.subType.damageClaimFiled!,
            documentationNotedIssue: dto.subType.documentationNotedIssue!,
            packageType: dto.subType.packageType!,
            packageTypeCondition: dto.subType.packageTypeCondition!,
            protectivePackagingUsed: dto.subType.protectivePackagingUsed!,
            deliveryDocumentation: CodMappingOperations.getAttachments(keys, 'deliveryDocumentation'),
            visualEvidence: CodMappingOperations.getAttachments(keys, 'visualEvidence'),
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapOrderEntryDomToForm(
        dto: ICodDomesticWithSubtype<IOrderEntrySubtypeDom>,
        keys: string[]
    ): CodCreateForm['orderEntryDomestic'] {
        return {
            ...CodMappingOperations.commonDomesticMapping(dto),
            issueType: dto.subType.issueType,
            accountErrorType: dto.subType.accountErrorType!,
            csrResponsible: dto.subType.csrGID!,
            correctOrderType: dto.subType.correctOrderType!,
            incorrectlyUsed: dto.subType.orderTypeIncorrectlyUsed!,
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapItemOrQuantityDomToForm(
        dto: ICodDomesticWithSubtype<IQuantitySubtypeDom>,
        keys: string[]
    ): CodCreateForm['itemOrQuantityDomestic'] {
        return {
            ...CodMappingOperations.commonDomesticMapping(dto),
            issueType: dto.subType.issueType,
            unorderedItemQuantity: dto.subType.unorderedItemQuantity!,
            unorderedItemSmn: dto.subType.unorderedItemSmn!,
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapProductDatingDomToForm(
        dto: ICodDomesticWithSubtype<IProductDatingSubtypeDom>,
        keys: string[]
    ): CodCreateForm['productDatingDomestic'] {
        return {
            ...CodMappingOperations.commonDomesticMapping(dto),
            issueType: dto.subType.issueType,
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapProductTemperatureDomToForm(
        dto: ICodDomesticWithSubtype<ITemperatureSubtypeDom>,
        keys: string[]
    ): CodCreateForm['productTemperatureDomestic'] {
        return {
            ...CodMappingOperations.commonDomesticMapping(dto),
            issueType: dto.subType.issueType,
            itemCondition: dto.subType.conditionOfItem,
            temperatureExcursionExplanation: dto.subType.explanation,
            temperatureExcursionEvidence: CodMappingOperations.getAttachments(keys, 'temperatureExcursionEvidence'),
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapDeliveryDomToForm(
        dto: ICodDomesticWithSubtype<IDeliverySubtypeDom>,
        keys: string[]
    ): CodCreateForm['deliveryErrorDomestic'] {
        return {
            ...CodMappingOperations.commonDomesticMapping(dto),
            issueType: dto.subType.issueType,
            deliveryDocumentationMatchesAddress: dto.subType.deliveryDocumentationIncorrectAddress ?? false,
            addressShippedTo: dto.subType.incorrectDeliveryAddress!,
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapInvoiceDomToForm(
        dto: ICodDomesticWithSubtype<IInvoiceSubtypeDom>,
        keys: string[]
    ): CodCreateForm['invoiceErrorDomestic'] {
        return {
            ...CodMappingOperations.commonDomesticMapping(dto),
            invoiceErrorType: dto.subType.issueType,
            invoiceErrorDetailType: dto.subType.invoiceErrorType!,
            instructionsContained: dto.subType.instructionsContained,
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapBillingAdjustmentDomToForm(
        dto: ICodDomesticWithSubtype<IBillingSubtypeDom>,
        keys: string[]
    ): CodCreateForm['billingAdjustmentDomestic'] {
        return {
            ...CodMappingOperations.commonDomesticMapping(dto),
            issueType: dto.subType.issueType,
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapEskerDomToForm(
        dto: ICodDomesticWithSubtype<IEskerSubtypeDom>,
        keys: string[]
    ): CodCreateForm['eskerDiscrepancyDomestic'] {
        return {
            ...CodMappingOperations.commonDomesticMapping(dto),
            issueType: dto.subType.issueType,
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapLargeInstrumentWarehouseDomToForm(
        dto: ICodDomesticWithSubtype<ILargeInstrumentWarehouseSubtypeDom>,
        keys: string[]
    ): CodCreateForm['largeInstrumentWarehouseDiscrepancy'] {
        return {
            ...CodMappingOperations.commonDomesticMapping(dto),
            issueType: dto.subType.issueType,
            claimFiled: dto.subType.claimFiled,
            itemCondition: dto.subType.conditionOfItem,
            packageType: dto.subType.packageType,
            packageTypeCondition: dto.subType.packageTypeCondition,
            documentationNotedIssue: dto.subType.potentialIssueNoted,
            additionalPackagingInfo: dto.subType.additionalPackagingInfo!,
            carrierClaimDocumentation: CodMappingOperations.getAttachments(keys, 'carrierClaimDocumentation'),
            deliveryDocumentation: CodMappingOperations.getAttachments(keys, 'deliveryDocumentation'),
            // TODO: Fix explanationOfMissingNote
            explanationOfMissingNote: '',
            visualEvidence: CodMappingOperations.getAttachments(keys, 'visualEvidence'),
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapProductDamageIntToForm(
        dto: ICodInternationalWithSubtype<IProductDamageSubtypeInt>,
        keys: string[]
    ): CodCreateForm['productDamageInternational'] {
        return {
            financialAdjustment: dto.financialAdjustmentRequest!,
            damageType: dto.subType.issueType,
            carrierTrackingNumber: dto.subType.carrierTrackingNumber,
            itemCondition: dto.subType.conditionOfItem,
            itemType: dto.subType.itemType,
            additionalPackagingInfo: dto.subType.additionalPackagingInfo!,
            canIdentifyCartonId: dto.subType.canIdentifyCartonId!,
            cartonId: dto.subType.cartonId!,
            damageClaimFiled: dto.subType.damageClaimFiled || false,
            documentationNotedIssue: dto.subType.documentationNotedIssue!,
            explanationOfMissingNote: dto.subType.explanationOfMissingNote!,
            packageType: dto.subType.packageType!,
            packageTypeCondition: dto.subType.packageTypeCondition!,
            protectivePackagingUsed: dto.subType.protectivePackagingUsed!,
            damageClaimDocumentation: CodMappingOperations.getAttachments(keys, 'damageClaimDocumentation'),
            deliveryDocumentation: CodMappingOperations.getAttachments(keys, 'deliveryDocumentation'),
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments'),
            visualEvidence: CodMappingOperations.getAttachments(keys, 'visualEvidence')
        };
    }

    private static mapOrderEntryIntToForm(
        dto: ICodInternationalWithSubtype<IOrderEntrySubtypeInt>,
        keys: string[]
    ): CodCreateForm['orderEntryInternational'] {
        return {
            financialAdjustment: dto.financialAdjustmentRequest!,
            issueType: dto.subType.issueType,
            accountErrorType: dto.subType.accountErrorType!,
            errorDescription: dto.subType.errorDescription!,
            canIdentifyCartonId: dto.subType.canIdentifyCartonId!,
            cartonId: dto.subType.cartonId!,
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapItemOrQuantityIntToForm(
        dto: ICodInternationalWithSubtype<IQuantitySubtypeInt>,
        keys: string[]
    ): CodCreateForm['itemOrQuantityInternational'] {
        return {
            financialAdjustment: dto.financialAdjustmentRequest!,
            additionalInfo: dto.additionalInfo!,
            issueType: dto.subType.issueType,
            unorderedItemQuantity: dto.subType.unorderedItemQuantity!,
            unorderedItemSmn: dto.subType.unorderedItemSmn!,
            canIdentifyCartonId: dto.subType.canIdentifyCartonId!,
            cartonId: dto.subType.cartonId!,
            lotsDeclinedWithin24Hours: dto.subType.lotsDeclinedWithin24Hours!,
            communicationDeclinedDocuments: CodMappingOperations.getAttachments(keys, 'communicationDeclinedDocuments'),
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapProductDatingIntToForm(
        dto: ICodInternationalWithSubtype<IProductDatingSubtypeInt>,
        keys: string[]
    ): CodCreateForm['productDatingInternational'] {
        return {
            financialAdjustment: dto.financialAdjustmentRequest!,
            issueType: dto.subType.issueType,
            declinedWithin24Hours: dto.subType.declinedWithin24Hours!,
            batchExpiryMeetsMinShipPolicy: dto.subType.batchExpiryMeetsMinShipPolicy!,
            communicationDeclinedDocuments: CodMappingOperations.getAttachments(keys, 'communicationDeclinedDocuments'),
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapProductTemperatureIntToForm(
        dto: ICodInternationalWithSubtype<ITemperatureSubtypeInt>,
        keys: string[]
    ): CodCreateForm['productTemperatureInternational'] {
        return {
            financialAdjustment: dto.financialAdjustmentRequest!,
            issueType: dto.subType.issueType,
            claimFiled: dto.subType.claimFiled,
            carrierTrackingNumber: dto.subType.carrierTrackingNumber,
            itemCondition: dto.subType.conditionOfItem,
            exactLocation: dto.subType.exactLocation,
            explainWhyTemperatureExcursionSuspected: dto.subType.explanation,
            temperatureExcursionIdentifiedBy: dto.subType.identificationBy,
            dateOfPotentialTemperatureExcursion: new Date(dto.subType.identificationDate).toISOString(),
            locationOfPotentialTemperatureExcursion: dto.subType.identificationLocation,
            documentationNotedIssue: dto.subType.issueNotedInDocumentation,
            includesShippingTemperatureRequirements: dto.subType.temperatureRequirementsIncluded,
            explanationOfMissingNote: dto.subType.issueNotNotedExplanation!,
            carrierClaimDocumentation: CodMappingOperations.getAttachments(keys, 'carrierClaimDocumentation'),
            deliveryDocumentation: CodMappingOperations.getAttachments(keys, 'deliveryDocumentation'),
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments'),
            temperatureExcursionEvidence: CodMappingOperations.getAttachments(keys, 'temperatureExcursionEvidence')
        };
    }

    private static mapDeliveryIntToForm(
        dto: ICodInternationalWithSubtype<IDeliverySubtypeInt>,
        keys: string[]
    ): CodCreateForm['deliveryErrorInternational'] {
        return {
            financialAdjustment: dto.financialAdjustmentRequest!,
            additionalInfo: dto.additionalInfo!,
            issueType: dto.subType.issueType,
            ainErrorType: dto.subType.ainErrorType!,
            addressShippedTo: dto.subType.incorrectDeliveryAddress!,
            deliveryDocumentationMatchesAddress: dto.subType.incorrectDeliveryInDocumentation!,
            replacementOrderRequired: dto.subType.replacementOrderRequired!,
            replacementOrderNumber: dto.subType.replacementOrderNumber!,
            carrier: dto.subType.wrongCarrier!,
            shipmentNumber: dto.subType.shipmentNumber!,
            deliveryDocumentation: CodMappingOperations.getAttachments(keys, 'deliveryDocumentation'),
            // TODO: Map this correctly
            deliveryDocumentationMatchesPort: false,
            portShippedTo: '',
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments'),
            supportDocumentation: CodMappingOperations.getAttachments(keys, 'supportDocumentation')
        };
    }

    private static mapInvoiceIntToForm(
        dto: ICodInternationalWithSubtype<IInvoiceSubtypeInt>,
        keys: string[]
    ): CodCreateForm['invoiceErrorInternational'] {
        return {
            financialAdjustment: dto.financialAdjustmentRequest!,
            additionalInfo: dto.additionalInfo!,
            invoiceErrorType: dto.subType.issueType,
            errorsIdentifiedWithin24hours: dto.subType.errorsIdentifiedWithin24hours,
            invoiceErrorTypeOtherInfo: dto.subType.invoiceErrorTypeOtherInfo!,
            invoiceMatchesIODC: dto.subType.invoiceMatchesIODC!,
            communicationDocuments: CodMappingOperations.getAttachments(keys, 'communicationDocuments'),
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapQualityIntToForm(
        dto: ICodInternationalWithSubtype<IQualitySubtypeInt>,
        keys: string[]
    ): CodCreateForm['qualityInternational'] {
        return {
            financialAdjustment: dto.financialAdjustmentRequest!,
            additionalInfo: dto.additionalInfo!,
            issueType: dto.subType.issueType,
            materialDisposition: dto.subType.materialDisposition,
            countrySpecificLabelMissing: dto.subType.countrySpecificLabelMissing!,
            labelError: dto.subType.labelError!,
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    private static mapDocumentationIntToForm(
        dto: ICodInternationalWithSubtype<IDocumentationSubtypeInt>,
        keys: string[]
    ): CodCreateForm['documentationInternational'] {
        return {
            financialAdjustment: dto.financialAdjustmentRequest!,
            issueType: dto.subType.issueType,
            documentationIssue: dto.subType.documentationIssue,
            documentationIssueOther: dto.subType.documentationIssueOtherInfo!,
            specialInstructions: dto.subType.containedSpecialInstructions!,
            documentationErrorType: dto.subType.documentationError!,
            documentationErrorTypeOther: dto.subType.documentationErrorOtherInfo!,
            relatedDocuments: CodMappingOperations.getAttachments(keys, 'relatedDocuments')
        };
    }

    public static getAttachments(keys: string[], prefix: string): CodAttachment[] {
        return keys.filter(key => key.startsWith(prefix)).map(key => ({ name: key.replace(prefix + '/', '') }));
    }

    private static addDocuments(key: string, attachments: CodAttachment[], documents: CodDocument[]): void {
        attachments?.forEach(attachment => {
            documents.push({
                file: attachment.file!,
                name: attachment.file?.name ?? attachment.name,
                key
            });
        });
    }

    private static commonDomesticMapping<T extends { issueType: string }>(
        dto: ICodDomesticWithSubtype<T>
    ): {
        adjustmentAmount: number;
        financialAdjustment: boolean;
        callTagTrackingNumber: string;
        additionalInfo: string;
        creditNumber: string;
        managerApprovedRequest: string;
        filedWithin30Days: boolean;
    } {
        return {
            adjustmentAmount: Number(dto.financialAdjustmentValue ?? 0),
            financialAdjustment: dto.filedWithin30Days != null ? true : Number(dto.financialAdjustmentValue ?? 0) !== 0,
            callTagTrackingNumber: dto.relatedTrackingNumber!,
            additionalInfo: dto.additionalInfo!,
            creditNumber: dto.relatedNumber!,
            managerApprovedRequest: dto.managerGid!,
            filedWithin30Days: !!dto.filedWithin30Days
        };
    }
}
